import React, { useState, useEffect } from 'react'
import Seo from '../components/Seo'
import Lightbox from '../components/Lightbox'
import { getCmsCollection, getCmsPage } from '../lib/cms'
import { getOptimizedImageUrl, getImageSrcSet } from '../lib/image'

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([])
  const [pageData, setPageData] = useState(null)
  const [activeCategory, setActiveCategory] = useState('Alle')
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentIdx, setCurrentIdx] = useState(0)

  useEffect(() => {
    async function loadData() {
      try {
        const [items, page] = await Promise.all([
          getCmsCollection('galleri'),
          getCmsPage('billedgalleri'),
        ])
        setGalleries(items)
        if (page) setPageData(page)
      } catch (err) {
        console.error('Failed to load galleries:', err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  // Collect all unique images or images for active category
  const allImages = React.useMemo(() => {
    const list = []
    const seen = new Set()

    galleries.forEach((gal) => {
      const cat = gal.category || 'Generelt'
      const matches = activeCategory === 'Alle' || cat === activeCategory

      if (matches && Array.isArray(gal.images)) {
        gal.images.forEach((img) => {
          if (!seen.has(img)) {
            seen.add(img)
            list.push({
              url: img,
              title: gal.title || 'Projektbillede',
              category: cat,
            })
          }
        })
      }
    })

    return list
  }, [galleries, activeCategory])

  // Extract unique categories for filter bar
  const categories = React.useMemo(() => {
    const set = new Set(['Alle'])
    galleries.forEach((g) => {
      if (g.category && g.category !== 'Hovedgalleri') {
        set.add(g.category)
      }
    })
    return Array.from(set)
  }, [galleries])

  const count = allImages.length

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat)
  }

  const openLightbox = (index) => {
    setCurrentIdx(index)
    setLightboxOpen(true)
  }

  const handleNext = () => {
    setCurrentIdx((prev) => (prev + 1) % allImages.length)
  }

  const handlePrev = () => {
    setCurrentIdx((prev) => (prev - 1 + allImages.length) % allImages.length)
  }

  return (
    <>
      <Seo
        title="Billedgalleri :: Lyn Entreprenør"
        description="Billedgalleri. Vores billedegalleri er en sand skattekiste for alle, der er interesserede i maskiner og udstyr til byggeri. Her finder du et bredt udvalg af billeder, der viser vores professionelle og moderne maskinpark i aktion. Vi er specialiserede i jordarbejde og vores billeder afspejler dette tydeligt."
        ogImage="https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/fulllogo-nobuffer-5_1e74d9a2.png"
        canonicalUrl="https://www.lyn-ent.dk/billedgalleri/"
      />

      {/* Header section (Red Subhero) */}
      <section className="bg-red-600 py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-left">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {pageData?.hero_title || 'Billedgalleri'}
          </h1>

          <p className="mt-4 text-base sm:text-lg text-red-100 max-w-3xl leading-relaxed">
            {pageData?.hero_description || 'Vores billedegalleri er en sand skattekiste for alle, der er interesserede i maskiner og udstyr til byggeri. Her finder du et bredt udvalg af billeder, der viser vores professionelle og moderne maskinpark i aktion. Vi er specialiserede i jordarbejde og vores billeder afspejler dette tydeligt.'}
          </p>

          {/* Filter tabs */}
          {categories.length > 1 && (
            <div className="mt-8 flex flex-wrap items-center gap-2 pt-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'bg-red-700/80 border border-red-500/80 text-white hover:bg-red-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Grid Section */}
      <section
        aria-label="Billedpanel oversigt"
        className="relative w-full bg-slate-950 overflow-hidden select-none"
      >
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-0 p-0 m-0 animate-pulse">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-4/3 sm:aspect-square md:aspect-4/3 bg-slate-900" />
            ))}
          </div>
        ) : count === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <p className="text-base">Ingen billeder fundet i denne kategori.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-0 p-0 m-0">
            {allImages.map((image, index) => (
              <div
                key={`${image.url}-${index}`}
                onClick={() => openLightbox(index)}
                className="group relative aspect-4/3 sm:aspect-square md:aspect-4/3 w-full overflow-hidden cursor-pointer bg-slate-900 focus:outline-none"
                role="button"
                tabIndex={0}
                aria-label={`Vis forstørret billede ${index + 1} af ${count}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openLightbox(index)
                  }
                }}
              >
                <img
                  src={getOptimizedImageUrl(image.url, { width: 800, quality: 80, format: 'auto' })}
                  srcSet={getImageSrcSet(image.url, [350, 600, 850])}
                  sizes="(max-width: 640px) 50vw, (max-width: 768px) 33.3vw, 25vw"
                  alt={image.title || 'Lyn Entreprenør'}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                {/* Subtle dark overlay that softens on hover, with zoom icon */}
                <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-slate-950/0 transition-colors duration-300 pointer-events-none" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <span className="w-10 h-10 rounded-full bg-red-600/90 text-white flex items-center justify-center shadow-lg shadow-black/40 transform scale-90 group-hover:scale-100 transition-transform">
                    <i className="fa-solid fa-magnifying-glass-plus text-sm" aria-hidden="true" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox component */}
      <Lightbox
        isOpen={lightboxOpen}
        images={allImages.map((o) => o.url)}
        currentIndex={currentIdx}
        title={allImages[currentIdx]?.title || 'Lyn Entreprenør ApS - Billedgalleri'}
        onClose={() => setLightboxOpen(false)}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </>
  )
}
